import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { MalayalamLayout } from "../components/malayalam/MalayalamLayout";
import { useMalayalamLang } from "../context/MalayalamLangContext";
import { C } from "../components/malayalam/mlShared";
import { mlMediaList, fileUrl } from "../lib/api";

export default function MalayalamGallery() {
  const { lang, t } = useMalayalamLang();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null); // lightbox

  useEffect(() => {
    let alive = true;
    mlMediaList("image")
      .then((d) => { if (alive) { setItems(d.items || []); setLoaded(true); } })
      .catch(() => { if (alive) { setItems([]); setLoaded(true); } });
    return () => { alive = false; };
  }, []);

  return (
    <MalayalamLayout>
      <section data-testid="ml-gallery-page">
        <div className="border-b" style={{ borderColor: C.line, background: "linear-gradient(180deg, #F3E9D2 0%, #FBF6EC 100%)" }}>
          <div className="mx-auto max-w-[1500px] px-6 py-10">
            <div className="mb-2 h-1 w-16 rounded-full" style={{ background: C.rust }} />
            <h1 className="font-display text-4xl font-bold sm:text-5xl" style={{ color: C.navy }} data-testid="ml-gallery-title">{t("Galleries")}</h1>
            <p className="mt-3 max-w-2xl text-base" style={{ color: C.muted }}>
              {t("Photographs, events, posters and captioned collections from the archive and our team.")}
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-[1500px] px-6 py-10">
          {!loaded ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.gold }} /></div>
          ) : items.length === 0 ? (
            <p className="rounded-lg border border-dashed px-5 py-10 text-center text-sm" style={{ borderColor: C.line, color: C.muted }} data-testid="ml-gallery-empty">
              {t("No photos have been added to the gallery yet.")}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="ml-gallery-grid">
              {items.map((it) => (
                <button key={it.id} onClick={() => setActive(it)} data-testid={`ml-gallery-item-${it.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border text-left transition-all hover:-translate-y-1 hover:shadow-lg"
                  style={{ background: C.card, borderColor: C.line }}>
                  <div className="overflow-hidden">
                    <img src={fileUrl(it.url)} alt={it.title || ""} loading="lazy"
                      className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  {(it.title || (it.tags && it.tags.length > 0)) && (
                    <div className="px-3 py-2">
                      {it.title && <div className="font-display text-sm font-semibold" style={{ color: C.navy }}>{it.title}</div>}
                      {it.tags && it.tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {it.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: "#F3E9D2", color: C.muted }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {active && (
          <div onClick={() => setActive(null)} data-testid="ml-gallery-lightbox"
            className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ background: "rgba(20,14,8,0.88)" }}>
            <button onClick={() => setActive(null)} className="absolute right-5 top-5 rounded-full p-2 text-white/90 hover:text-white" data-testid="ml-gallery-close" style={{ background: "rgba(0,0,0,0.4)" }}>
              <X className="h-6 w-6" />
            </button>
            <figure onClick={(e) => e.stopPropagation()} className="max-h-[88vh] max-w-4xl">
              <img src={fileUrl(active.url)} alt={active.title || ""} className="max-h-[80vh] w-auto rounded-lg object-contain" />
              {active.title && <figcaption className="mt-3 text-center text-sm text-white/85">{active.title}</figcaption>}
            </figure>
          </div>
        )}
      </section>
    </MalayalamLayout>
  );
}
